/* "How we count": long-form text on the terminal skin. Colors come from the
   variables in terminal.css; corners stay square (set globally). */

.page {
  max-width: 1160px;
  margin: 0 auto;
  padding: 20px 0 24px;
}

.intro {
  max-width: 72ch;
  margin-bottom: 32px;
}

.eyebrow {
  margin: 0 0 10px;
  color: var(--signal);
  font-size: 0.72rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.intro h1 {
  margin: 0;
  font-size: clamp(1.6rem, 3vw, 2.3rem);
  font-weight: 600;
  line-height: 1.15;
}

.lede {
  margin: 12px 0 0;
  font-size: 1rem;
  line-height: 1.6;
  text-wrap: pretty;
}

.reviewed {
  margin: 12px 0 0;
  color: var(--ink-muted);
  font-size: 0.78rem;
}

.page a {
  color: var(--signal);
  text-underline-offset: 4px;
}

.page code {
  padding: 0 3px;
  background: var(--paper-raised);
  border: 1px solid var(--line);
  color: var(--ink);
  font-size: 0.92em;
  overflow-wrap: anywhere;
}

.layout {
  display: grid;
  grid-template-columns: minmax(170px, 210px) minmax(0, 1fr);
  align-items: start;
  gap: 44px;
}

.toc {
  position: sticky;
  top: 16px;
  border-top: 1px solid var(--line-strong);
}

.tocTitle {
  margin: 12px 0 4px;
  color: var(--ink-muted);
  font-size: 0.7rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.toc ol {
  margin: 0;
  padding: 0;
  list-style: none;
}

.toc a {
  display: flex;
  align-items: center;
  min-height: 32px;
  color: var(--ink-muted);
  font-size: 0.84rem;
  text-decoration: none;
}

.toc a:hover {
  color: var(--signal);
}

.content {
  min-width: 0;
}

/* Running text keeps a comfortable line length; the venue table and cards
   may use the full column. */
.section > p,
.points,
.metrics {
  max-width: 76ch;
}

.section {
  padding: 22px 0 8px;
  border-top: 1px solid var(--line-strong);
  scroll-margin-top: 16px;
}

.section:first-child {
  padding-top: 12px;
}

.section h2 {
  margin: 0 0 14px;
  color: var(--signal);
  font-size: 1.1rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.section p,
.points li,
.metric dd,
.venueFacts dd {
  font-size: 0.9rem;
  line-height: 1.65;
  text-wrap: pretty;
}

.section p {
  margin: 0 0 14px;
}

.points {
  margin: 0 0 16px;
  padding-left: 1.2em;
}

.points li + li {
  margin-top: 8px;
}

.points li::marker {
  color: var(--signal);
}

/* ---- metric definitions ---- */

.metrics {
  margin: 0;
}

.metric {
  padding: 12px 0;
  border-top: 1px solid var(--line);
}

.metric:first-child {
  border-top: 0;
  padding-top: 0;
}

.metric dt {
  margin-bottom: 4px;
  color: var(--ink);
  font-weight: 600;
}

.metric dd {
  margin: 0;
  color: var(--ink);
}

.bases {
  margin: 10px 0 12px;
  border: 1px solid var(--line);
  background: var(--paper-raised);
}

.bases > div {
  display: grid;
  grid-template-columns: 150px minmax(0, 1fr);
  gap: 12px;
  padding: 8px 10px;
}

.bases > div + div {
  border-top: 1px solid var(--line);
}

.bases dt {
  color: var(--signal);
  font-size: 0.82rem;
}

.bases dd {
  margin: 0;
  color: var(--ink-muted);
  font-size: 0.84rem;
  line-height: 1.55;
}

/* ---- venues at a glance ---- */

.tableScroll {
  overflow-x: auto;
  margin-bottom: 16px;
  border: 1px solid var(--line-strong);
}

.table {
  width: 100%;
  min-width: 720px;
  border-collapse: collapse;
  font-size: 0.8rem;
}

.table th,
.table td {
  padding: 7px 10px;
  border-bottom: 1px solid var(--line);
  text-align: left;
  vertical-align: top;
}

.table thead th {
  background: var(--head-fill);
  color: var(--signal);
  font-size: 0.68rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  white-space: nowrap;
}

.table tbody th {
  font-weight: 600;
  white-space: nowrap;
}

.table tbody tr:last-child > * {
  border-bottom: 0;
}

.table td {
  color: var(--ink-muted);
}

/* ---- venue by venue ---- */

.venueList {
  margin: 0;
  padding: 0;
  list-style: none;
}

.venue {
  display: grid;
  grid-template-columns: 150px minmax(0, 1fr);
  gap: 16px;
  padding: 16px 0;
  border-top: 1px solid var(--line);
  scroll-margin-top: 16px;
}

.venueList > li:first-child .venue {
  border-top: 0;
  padding-top: 0;
}

.venueIdentity h3 {
  margin: 0;
  color: var(--ink);
  font-size: 1rem;
}

.venueIdentity p {
  margin: 4px 0 0;
  color: var(--ink-muted);
  font-size: 0.78rem;
  line-height: 1.5;
}

.venueFacts {
  margin: 0;
}

.venueFacts > div {
  display: grid;
  grid-template-columns: 120px minmax(0, 1fr);
  gap: 12px;
  padding: 4px 0;
}

.venueFacts dt {
  padding-top: 2px;
  color: var(--signal);
  font-size: 0.72rem;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.venueFacts dd {
  margin: 0;
}

/* ---- narrow screens ---- */

@media (max-width: 860px) {
  .layout {
    grid-template-columns: minmax(0, 1fr);
    gap: 16px;
  }

  .toc {
    position: static;
  }

  .toc ol {
    display: flex;
    flex-wrap: wrap;
    gap: 0 16px;
  }
}

@media (max-width: 600px) {
  .page {
    padding-top: 12px;
  }

  .venue,
  .venueFacts > div,
  .bases > div {
    grid-template-columns: minmax(0, 1fr);
    gap: 4px;
  }

  .venueFacts > div {
    padding: 6px 0;
  }
}
